import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #variantMediaMap = {};

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#variantMediaMap = this.#parseVariantMediaMap();
    this.#syncThumbnailControls();

    if (this.dataset.currentVariantId && this.#hasLocalVariantFiltering) {
      queueMicrotask(() => {
        this.#applyVariantMedia(this.dataset.currentVariantId);
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const productId = event.detail.data.productId;
    const newProductId = event.detail.data.newProduct?.id;

    if (newProductId || (productId && this.dataset.productId && productId !== this.dataset.productId)) {
      return this.#replaceFromServerResponse(event);
    }

    const variantId = `${event.detail.resource?.id ?? ''}`;
    const activeMediaIds = this.#variantMediaMap?.[variantId];

    if (!variantId || !this.#hasLocalVariantFiltering || !Array.isArray(activeMediaIds) || activeMediaIds.length === 0) {
      return this.#replaceFromServerResponse(event);
    }

    this.dataset.currentVariantId = variantId;
    this.#applyVariantMedia(variantId);
  };

  #replaceFromServerResponse(event) {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  get #hasLocalVariantFiltering() {
    return Object.keys(this.#variantMediaMap).length > 0;
  }

  #parseVariantMediaMap() {
    const textContent = this.refs.variantMediaMap?.textContent?.trim();

    if (!textContent) return {};

    try {
      return JSON.parse(textContent);
    } catch (error) {
      console.error('Failed to parse variant media map', error);
      return {};
    }
  }

  #applyVariantMedia(variantId) {
    const activeMediaIds = this.#variantMediaMap?.[variantId];

    if (!Array.isArray(activeMediaIds) || activeMediaIds.length === 0) return;

    const orderedMediaIds = activeMediaIds.map((id) => `${id}`);

    this.refs.zoomDialogComponent?.closeDialog?.();

    this.#reorderMediaElements(this.querySelector('slideshow-slides'), orderedMediaIds, (element, index) => {
      element.setAttribute('aria-hidden', `${index !== 0}`);
      element.removeAttribute('reveal');
    });

    this.#reorderMediaElements(this.querySelector('.media-gallery__grid'), orderedMediaIds, (element, index) => {
      const zoomButton = element.querySelector('.product-media-container__zoom-button');

      if (zoomButton instanceof HTMLElement) {
        zoomButton.setAttribute('on:click', `/zoom/${index}`);
        zoomButton.setAttribute('on:pointerenter', `/preloadImage/${index}`);
      }
    });

    for (const thumbnailGroup of this.querySelectorAll('.slideshow-controls__thumbnails')) {
      this.#reorderMediaElements(thumbnailGroup, orderedMediaIds, (element, index) => {
        element.setAttribute('on:click', `/select/${index}`);
        element.setAttribute('aria-selected', `${index === 0}`);
      });
    }

    this.#reorderMediaElements(this.querySelector('.dialog-thumbnails-list'), orderedMediaIds, (element, index) => {
      element.setAttribute('on:click', `/handleThumbnailClick/${index}`);
      element.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
      element.setAttribute('aria-selected', `${index === 0}`);
    });

    this.#reorderMediaElements(this.querySelector('.dialog-zoomed-gallery'), orderedMediaIds);
    this.#syncDotControls(orderedMediaIds.length);
    this.#syncThumbnailControls();

    this.updatedCallback();
    this.slideshow?.refresh?.(0);
    this.refs.zoomDialogComponent?.updatedCallback?.();
  }

  #reorderMediaElements(container, orderedMediaIds, onActivate) {
    if (!(container instanceof HTMLElement)) return;

    const mediaIdSet = new Set(orderedMediaIds);
    const children = Array.from(container.children).filter((child) => child instanceof HTMLElement);
    const elementsByMediaId = new Map(
      children
        .map((child) => [child.dataset.mediaId, child])
        .filter(([mediaId]) => typeof mediaId === 'string' && mediaId.length > 0)
    );

    orderedMediaIds.forEach((mediaId, index) => {
      const element = elementsByMediaId.get(mediaId);

      if (!(element instanceof HTMLElement)) return;

      element.hidden = false;
      container.append(element);
      onActivate?.(element, index);
    });

    children.forEach((child) => {
      const mediaId = child.dataset.mediaId;

      if (!mediaIdSet.has(`${mediaId ?? ''}`)) {
        child.hidden = true;
        child.setAttribute('aria-hidden', 'true');
        container.append(child);
      }
    });
  }

  #syncDotControls(activeCount) {
    const dotItems = Array.from(this.querySelectorAll('.slideshow-controls__dots > li'));

    dotItems.forEach((item, index) => {
      const button = item.querySelector('button');
      const active = index < activeCount;

      item.hidden = !active;

      if (button instanceof HTMLElement && active) {
        button.setAttribute('on:click', `/select/${index}`);
        button.setAttribute('aria-selected', `${index === 0}`);
      }
    });
  }

  #syncThumbnailControls() {
    const thumbnailControls = Array.from(this.querySelectorAll('.product-media-gallery__thumbnail-controls'));

    thumbnailControls.forEach((control) => {
      if (!(control instanceof HTMLElement)) return;

      const visibleThumbnails = Array.from(control.querySelectorAll('.slideshow-controls__thumbnail')).filter(
        (thumbnail) => thumbnail instanceof HTMLElement && !thumbnail.hasAttribute('hidden')
      );

      const visibleCount = visibleThumbnails.length;
      control.style.setProperty('--visible-thumbnail-count', `${Math.max(visibleCount, 1)}`);
      control.hidden = visibleCount <= 1;
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
